import { useCallback, useContext, useState } from 'react'

import { useTheme } from '@emotion/react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AllocationDistributionDestinationAndShare } from '@cash-web/protos/squareup/cash/paychecks/AllocationDistribution.pb'
import { SetPaycheckAllocationAmountInputsSubmissionInteraction } from '@cash-web/protos/squareup/cash/paychecks/api/blockers/v1/set_paycheck_allocation_amount.pb'
import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import {
  SetPaycheckAllocationAmountBlocker,
  SetPaycheckAllocationAmountBlockerDestinationUiConfigurationSelectedStateUIElements,
} from '@cash-web/protos/squareup/franklin/blockers/set_paycheck_allocation_amount_blocker.pb'
import { useMutationSubmitSetPaycheckAllocationAmount } from '@cash-web/shared-data-access-api/react-query/useMutationSubmitSetPaycheckAllocationAmount'
import { useCurrencySymbol } from '@cash-web/shared-feature-currency-symbol'
import {
  DISTRIBUTION_COLORS_DARK,
  DISTRIBUTION_COLORS_LIGHT,
  DISTRIBUTION_IDS,
  getDestinationId,
} from '@cash-web/shared-feature-paychecks'
import { createArcs } from '@cash-web/shared-feature-pie-chart'
import { FlowContext } from '@cash-web/shared-flow-data-access-context'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BaseBlockerFormFields } from '..'

export const useBlockerSetAllocationAmount = (blocker: SetPaycheckAllocationAmountBlocker) => {
  const { t } = useTranslation()
  const { darkMode } = useTheme()
  const [shouldShowManualInput, setShouldShowManualInput] = useState(false)
  const [shouldShowExceededMaxDistributionAlert, setShouldShowExceededMaxDistributionAlert] = useState(false)
  const currencySymbol = useCurrencySymbol()
  const destinationState =
    blocker.initially_selected_destination_index !== undefined
      ? blocker.initial_destination_states?.[blocker.initially_selected_destination_index]
      : undefined
  const uiElements: SetPaycheckAllocationAmountBlockerDestinationUiConfigurationSelectedStateUIElements =
    destinationState?.selected_state_ui_elements ?? {}
  const initialDestination =
    blocker.initially_selected_destination_index !== undefined
      ? blocker.initial_destination_states?.[blocker.initially_selected_destination_index]
      : undefined
  const allocationDestination = initialDestination?.destination
  const initialAllocations = blocker.initial_destination_states?.map(
    ({ destination: allocation_destination, share_in_basis_points }) => {
      return {
        allocation_destination,
        share_in_basis_points,
      }
    }
  )
  const [allocations, setAllocations] = useState<AllocationDistributionDestinationAndShare[]>(initialAllocations ?? [])
  const selectedDestination = allocationDestination
    ? DISTRIBUTION_IDS.find(type => allocationDestination[type] !== undefined)
    : undefined
  const form = useForm<BaseBlockerFormFields>({ mode: 'onTouched' })
  const flow = useFlowContext()
  const clientScenario = flow.active?.clientScenario
  const flowToken = flow.active?.flowToken
  const { mutateAsync: setPaycheckAllocationAmount } = useMutationSubmitSetPaycheckAllocationAmount({
    options: { clientScenario, flowToken },
  })
  const { updateWithResponse } = useContext(FlowContext)

  const handleBasisChange = (basis: number) => {
    if (!selectedDestination) {
      return
    }

    const maxBasis = Math.min(basis, totalAvailableAllocation)

    const updatedAllocations = allocations?.map(allocation =>
      allocation.allocation_destination?.[selectedDestination]
        ? { ...allocation, share_in_basis_points: maxBasis }
        : allocation
    )

    const totalOfNewAllocationsWithoutCashBalance = updatedAllocations?.reduce(
      (acc, { allocation_destination, share_in_basis_points }) =>
        allocation_destination?.['cashBalance'] ? acc : acc + (share_in_basis_points ?? 0),
      0
    )

    const finalAllocations = updatedAllocations?.map(allocation =>
      allocation.allocation_destination?.['cashBalance']
        ? { ...allocation, share_in_basis_points: 10000 - totalOfNewAllocationsWithoutCashBalance }
        : allocation
    )

    setAllocations(finalAllocations)
  }

  const allocationBasis = selectedDestination
    ? allocations?.find(allocation => allocation.allocation_destination?.[selectedDestination])?.share_in_basis_points
    : undefined

  const submitForm = useCallback(
    async ({
      submission_interaction,
    }: {
      submission_interaction: SetPaycheckAllocationAmountInputsSubmissionInteraction
    }) => {
      await updateWithResponse({
        response: setPaycheckAllocationAmount({
          blocker_inputs: {
            submission_interaction,
            selected_destination: allocationDestination ?? undefined,
            updated_distribution: {
              allocations,
            },
          },
          request_context: {
            blocker_descriptor_id: 'SET_PAYCHECK_ALLOCATION_AMOUNT',
          },
        }),
        clientScenario: ClientScenario.PLASMA,
        queriesToInvalidate: [],
      })
    },
    [allocationDestination, allocations, setPaycheckAllocationAmount, updateWithResponse]
  )

  const handleDisableButtonClick = async () => {
    form.handleSubmit(() =>
      submitForm({
        submission_interaction:
          SetPaycheckAllocationAmountInputsSubmissionInteraction.SUBMISSION_INTERACTION_BUTTON_TAP_DISABLE,
      })
    )()
  }

  const onSubmit = () => {
    form.handleSubmit(() =>
      submitForm({
        submission_interaction:
          allocationBasis === 0
            ? SetPaycheckAllocationAmountInputsSubmissionInteraction.SUBMISSION_INTERACTION_BUTTON_TAP_DISABLE
            : SetPaycheckAllocationAmountInputsSubmissionInteraction.SUBMISSION_INTERACTION_BUTTON_TAP_CONFIRM,
      })
    )()
  }

  const onBack = useCallback(() => {
    form.handleSubmit(() =>
      submitForm({
        submission_interaction: SetPaycheckAllocationAmountInputsSubmissionInteraction.SUBMISSION_INTERACTION_CANCEL,
      })
    )()
  }, [form, submitForm])

  const onEditInvestmentEntity = () => {
    form.handleSubmit(() =>
      submitForm({
        submission_interaction:
          SetPaycheckAllocationAmountInputsSubmissionInteraction.SUBMISSION_INTERACTION_CONFIGURATION_ROW_TAP,
      })
    )()
  }

  const basisParsed = `${allocationBasis ? allocationBasis / 100 : 0}%`
  const [formattedValue, setFormattedValue] = useState<string>(basisParsed)
  const contributionBasedOnAverage =
    blocker.average_monthly_paycheck && blocker.average_monthly_paycheck.amount && allocationBasis
      ? blocker.average_monthly_paycheck.amount * (allocationBasis / 1000000)
      : 0
  const contributionBasedOnAverageParsed = `${currencySymbol}${parseFloat(`${contributionBasedOnAverage}`).toFixed(2)}`
  const estimatedCurrencyAmountTextTranslatedValue =
    uiElements.estimated_currency_amount_template?.android?.translated_value
  const estimatedCurrencyAmountText = estimatedCurrencyAmountTextTranslatedValue?.replace(
    '%s',
    contributionBasedOnAverageParsed
  )

  const shouldSubmitBeDisabled = allocationBasis === undefined || allocationBasis < 0

  const totalAvailableAllocation = selectedDestination
    ? allocations?.reduce(
        (acc, { allocation_destination, share_in_basis_points }) =>
          allocation_destination?.[selectedDestination] ?? allocation_destination?.['cashBalance']
            ? acc
            : acc - (share_in_basis_points ?? 0),
        10000
      )
    : 0

  const handleSetFormattedValue = (value: string) => {
    const basis = Number(value.replace('%', '')) * 100
    if (basis < 0 || Number.isNaN(basis)) {
      setFormattedValue('')
      return
    }
    setFormattedValue(value)
    handleBasisChange(basis)
  }

  const handleExceededMaxDistributionAlertAcknowledge = () => {
    setShouldShowExceededMaxDistributionAlert(false)
  }

  const distributionColors = darkMode ? DISTRIBUTION_COLORS_DARK : DISTRIBUTION_COLORS_LIGHT
  const slices = allocations.map(({ allocation_destination, share_in_basis_points }) => {
    const id = (allocation_destination && getDestinationId(allocation_destination)) ?? ''
    return {
      id,
      label: id,
      color: id && distributionColors[id],
      percentage: (share_in_basis_points ?? 0) / 100,
    }
  })
  const sortedSlices = slices.sort((a, b) => {
    if (a.id === 'cashBalance') return 1
    if (b.id === 'cashBalance') return -1
    return b.percentage - a.percentage
  })
  const arcs = createArcs(sortedSlices)
  const icon = destinationState?.destination?.investing?.ui_specification?.icon
  const hero = {
    color: selectedDestination ? distributionColors[selectedDestination] : 'inherit',
    value: basisParsed,
    label: t('of each paycheck', { context: 'label' }),
    avatar_url: darkMode ? icon?.dark_url : icon?.light_url ?? icon?.light_url,
    iconUnicode: destinationState?.destination?.savings?.ui_specification?.icon_unicode,
  }

  const [customAmount, setCustomAmount] = useState(formattedValue)
  const closeCustomAmountModal = () => {
    handleSetFormattedValue(customAmount)
    setShouldShowManualInput(false)
  }

  return {
    form,
    allocationBasis,
    closeCustomAmountModal,
    customAmount,
    investingDestination: destinationState?.destination?.investing,
    setCustomAmount,
    shouldSubmitBeDisabled,
    setShouldShowManualInput,
    shouldShowManualInput,
    handleSetFormattedValue,
    formattedValue,
    handleDisableButtonClick,
    onEditInvestmentEntity,
    onSubmit,
    onBack,
    basisParsed,
    estimatedCurrencyAmountText,
    shouldShowExceededMaxDistributionAlert,
    handleExceededMaxDistributionAlertAcknowledge,
    uiElements,
    totalAvailableAllocation,
    handleBasisChange,
    arcs,
    hero,
    selectedDestination,
  }
}
